<template>
  <section class="privacy-policy-page">
    <div class="privacy-policy-page__toolbar">
      <div
        class="privacy-policy-page__languages"
        role="group"
        aria-label="Privacy Policy language"
      >
        <button
          v-for="item in languages"
          :key="item.code"
          type="button"
          class="privacy-policy-page__language"
          :class="{ 'privacy-policy-page__language--active': activeLang === item.code }"
          :aria-pressed="activeLang === item.code ? 'true' : 'false'"
          @click="selectLanguage(item.code)"
        >
          {{ item.label }}
        </button>
      </div>

      <a
        class="privacy-policy-page__direct-link"
        :href="policyUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ directLinkLabel }}
      </a>
    </div>

    <iframe
      ref="policyFrame"
      :key="policyUrl"
      class="privacy-policy-page__frame"
      :src="policyUrl"
      :title="frameTitle"
      :style="{ height: `${frameHeight}px` }"
      scrolling="no"
      @load="resizeFrame"
    />

    <noscript>
      <p>
        <a href="/static/legal/privacy/tr.html">SASAHUB Privacy Policy</a>
      </p>
    </noscript>
  </section>
</template>

<script>
const SUPPORTED_LANGUAGES = ["tr", "ru", "en"];

const LABELS = {
  tr: {
    frameTitle: "SASAHUB Gizlilik Politikası",
    directLink: "Belgeyi ayrı aç",
  },
  ru: {
    frameTitle: "SASAHUB — Политика конфиденциальности",
    directLink: "Открыть документ отдельно",
  },
  en: {
    frameTitle: "SASAHUB Privacy Policy",
    directLink: "Open document separately",
  },
};

export default {
  name: "PrivacyPolicyPage",

  data() {
    return {
      selectedLang: null,
      frameHeight: 1600,
      resizeTimer: null,
      languages: [
        { code: "tr", label: "TR" },
        { code: "ru", label: "RU" },
        { code: "en", label: "EN" },
      ],
    };
  },

  computed: {
    storeLang() {
      const session = this.$store && this.$store.state
        ? this.$store.state.session
        : null;
      const lang = session && session.lang;

      return SUPPORTED_LANGUAGES.includes(lang) ? lang : "tr";
    },

    activeLang() {
      return this.selectedLang || this.storeLang;
    },

    policyUrl() {
      return `/static/legal/privacy/${this.activeLang}.html`;
    },

    frameTitle() {
      return LABELS[this.activeLang].frameTitle;
    },

    directLinkLabel() {
      return LABELS[this.activeLang].directLink;
    },
  },

  watch: {
    activeLang() {
      this.frameHeight = 1600;
    },
  },

  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.scheduleResize);
    }
  },

  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.scheduleResize);
    }

    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
  },

  methods: {
    selectLanguage(lang) {
      if (!SUPPORTED_LANGUAGES.includes(lang)) {
        return;
      }

      this.selectedLang = lang;
    },

    scheduleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = setTimeout(() => {
        this.resizeTimer = null;
        this.resizeFrame();
      }, 100);
    },

    resizeFrame() {
      this.$nextTick(() => {
        const frame = this.$refs.policyFrame;

        if (!frame) {
          return;
        }

        try {
          const doc = frame.contentDocument ||
            (frame.contentWindow && frame.contentWindow.document);

          if (!doc) {
            return;
          }

          const bodyHeight = doc.body ? doc.body.scrollHeight : 0;
          const documentHeight = doc.documentElement
            ? doc.documentElement.scrollHeight
            : 0;

          this.frameHeight = Math.max(bodyHeight, documentHeight, 1600);
        } catch (error) {
          this.frameHeight = Math.max(this.frameHeight, 1600);
        }
      });
    },
  },
};
</script>

<style lang="stylus" scoped>
.privacy-policy-page {
  width 100%
  min-height 100vh
  background #f5f7f8

  &__toolbar {
    width 100%
    max-width 980px
    margin 0 auto
    padding 20px 20px 0
    display flex
    align-items center
    justify-content space-between
    gap 16px
    flex-wrap wrap
  }

  &__languages {
    display flex
    gap 8px
  }

  &__language {
    min-width 46px
    height 38px
    padding 0 14px
    border 1px solid #d8e1e4
    border-radius 6px
    background #ffffff
    color #24484a
    font-weight 600
    cursor pointer

    &--active {
      background #237f7c
      border-color #237f7c
      color #ffffff
    }
  }

  &__direct-link {
    color #146b69
    font-weight 600
    text-decoration underline
    text-underline-offset 2px
  }

  &__frame {
    display block
    width 100%
    min-height 1600px
    border 0
    background #f5f7f8
  }
}
</style>
